import { NudosStaticAlertInComponent } from 'components/NudosComponents';
import {
  IQuoteEditorFormData,
  IQuoteItemDetailsForQuoteManagement,
  IQuotePricingConfig,
  IRequoteParentData,
  ISuggestedToolData
} from 'types/quoteEditor.types';
import RequestedDevice from '../RequestedDevice/RequestedDevice';
import QuoteSuggestedTool from '../QuoteSuggestedTool/QuoteSuggestedTool';
import AddSuggestionButton from './components/AddSuggestionButton/AddSuggestionButton';
import { BordTextButton } from 'bord-design-system';
import { useModal } from 'hooks/useModal';
import SuggestReasonModal from '../SuggestReasonModal/SuggestReasonModal';
import './QuoteItemEditor.scss';

interface IQuoteItemEditor {
  selectedItem: IQuoteItemDetailsForQuoteManagement;
  currentFormData: IQuoteEditorFormData;
  currentSuggestions: ISuggestedToolData[];
  remainingSuggestions: number;
  maxSuggestionsPerTool: number;
  countryName: string;
  countryCode: string;
  pricingConfig: IQuotePricingConfig;
  isRequote?: boolean;
  isFormDisabled?: boolean;
  parentData?: IRequoteParentData;
  onFormChange: (data: IQuoteEditorFormData) => void;
  onSuggestionChange: (suggestion: ISuggestedToolData) => void;
  onRemoveSuggestion: (id: number) => void;
  onAddSuggestion: () => void;
  onToolCreated: () => void;
  onChangeReason: (reasonId: number, reasonName: string) => void;
}

/**
 * Panel de edición de cotización para la herramienta actualmente seleccionada.
 * Muestra la solicitud del cliente y el formulario de edición. Siempre permite
 * agregar herramientas sugeridas alternativas, independientemente de si el ítem
 * es genérico o no. En ítems genéricos se requiere al menos una sugerencia completa;
 * en ítems no genéricos las sugerencias son opcionales pero deben estar completas si se agregan.
 */
const QuoteItemEditor = ({
  selectedItem,
  currentFormData,
  currentSuggestions,
  remainingSuggestions,
  maxSuggestionsPerTool,
  countryName,
  countryCode,
  pricingConfig,
  isRequote,
  isFormDisabled,
  parentData,
  onFormChange,
  onSuggestionChange,
  onRemoveSuggestion,
  onAddSuggestion,
  onToolCreated,
  onChangeReason
}: IQuoteItemEditor) => {
  const { isOpen: isSuggestReasonOpen, open: openSuggestReason, close: closeSuggestReason } = useModal();
  const hasSuggestions = currentSuggestions.length > 0;
  const isGenericItem = !!selectedItem?.isGenericProduct;
  const firstReason = currentSuggestions.find(s => s.reason)?.reason;
  const displayReason = firstReason || (isGenericItem ? 'También podría funcionarte' : '');
  const shouldShowReasonBanner = !!displayReason && hasSuggestions && !!onChangeReason;

  const handleConfirmSuggestReason = (reasonId: number, reasonName: string) => {
    onChangeReason(reasonId, reasonName);
    closeSuggestReason();
  };

  return (
    <div className="quoteItemEditor">
      {isSuggestReasonOpen && (
        <SuggestReasonModal device={selectedItem} onClose={closeSuggestReason} onConfirm={handleConfirmSuggestReason} />
      )}
      <RequestedDevice
        item={selectedItem}
        formData={currentFormData}
        countryName={countryName}
        pricingConfig={pricingConfig}
        isRequote={isRequote}
        isFormDisabled={isFormDisabled}
        parentData={parentData}
        onFormChange={onFormChange}
      />
      {shouldShowReasonBanner && (
        <NudosStaticAlertInComponent
          alertOptions={{}}
          color="yellow"
          type="warning"
          customClassname="reasonBanner"
          bodyAlertComponent={
            <div className="reasonBannerContent">
              <div className="reasonBannerText">
                <span className="reasonBannerLabel">Razón de las sugerencias:</span> {displayReason}
              </div>
              {!isFormDisabled && (
                <BordTextButton
                  text="Cambiar"
                  className="reasonBannerButton"
                  onClick={openSuggestReason}
                  rightIconProps={{ variant: 'refreshCw', standardSize: 12 }}
                />
              )}
            </div>
          }
        />
      )}
      <div className="suggestedToolsContainer">
        {currentSuggestions.map((suggestion, index) => (
          <QuoteSuggestedTool
            key={suggestion.reactKey}
            suggestionIndex={index}
            data={suggestion}
            canRemove={selectedItem.isGenericProduct ? currentSuggestions.length > 1 : true}
            requestedUnits={selectedItem.quantity}
            countryCode={countryCode}
            quoteItemId={selectedItem.quoteItemId}
            pricingConfig={pricingConfig}
            isFormDisabled={isFormDisabled}
            onChange={onSuggestionChange}
            onRemove={() => onRemoveSuggestion(suggestion.reactKey)}
            onToolCreated={onToolCreated}
          />
        ))}
        {remainingSuggestions > 0 && !isFormDisabled && (
          <AddSuggestionButton
            currentCount={currentSuggestions.length}
            maxCount={maxSuggestionsPerTool}
            onClick={onAddSuggestion}
          />
        )}
      </div>
    </div>
  );
};

export default QuoteItemEditor;
